import React, { useState, useEffect } from 'react';
import {
  getAiConnectors,
  createAiConnector,
  updateAiConnector,
  deleteAiConnector,
} from '../services/api';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import MaterialIcon from '../components/MaterialIcon';
import type { LayoutProps } from '../types/layout';

const SIMPLE_ICONS_CDN = 'https://cdn.simpleicons.org';

const PROVIDERS: {
  id: string;
  name: string;
  subtitle: string;
  placeholder: string;
  color: string;
  /** Simple Icons slug pre cdn.simpleicons.org/{slug}/{color}, alebo null = použiť Material icon */
  logoSlug: string | null;
  icon: string;
}[] = [
  { id: 'openai', name: 'OpenAI', subtitle: 'ChatGPT / OpenAI-compatible', placeholder: 'sk-…', color: '#10a37f', logoSlug: 'openai', icon: 'bolt' },
  { id: 'anthropic', name: 'Anthropic', subtitle: 'Claude', placeholder: 'sk-ant-…', color: '#d97706', logoSlug: 'anthropic', icon: 'psychology' },
  { id: 'google', name: 'Google AI', subtitle: 'Gemini', placeholder: 'API key', color: '#4285F4', logoSlug: 'google', icon: 'auto_awesome' },
  { id: 'groq', name: 'Groq', subtitle: 'Fast inference', placeholder: 'gsk_…', color: '#7c3aed', logoSlug: 'groq', icon: 'speed' },
  { id: 'together', name: 'Together AI', subtitle: 'Open models', placeholder: 'API key', color: '#06b6d4', logoSlug: 'togetherai', icon: 'hub' },
  { id: 'mistral', name: 'Mistral AI', subtitle: 'Mistral models', placeholder: 'API key', color: '#f59e0b', logoSlug: 'mistral', icon: 'flare' },
  { id: 'custom', name: 'Custom', subtitle: 'Custom endpoint', placeholder: 'API key', color: '#64748b', logoSlug: null, icon: 'link' },
];

interface AiConnectorItem {
  _id: string;
  provider: string;
  name?: string;
  baseUrl?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

function ProviderLogo({ provider }: { provider: (typeof PROVIDERS)[0] }) {
  if (provider.logoSlug) {
    const src = `${SIMPLE_ICONS_CDN}/${provider.logoSlug}/${provider.color.replace('#', '')}`;
    return (
      <span className="ai-connector-provider-logo-wrap">
        <img
          src={src}
          alt=""
          className="ai-connector-provider-logo"
          onError={(e) => {
            const t = e.target as HTMLImageElement;
            t.style.display = 'none';
            const fallback = t.nextElementSibling as HTMLElement;
            if (fallback) fallback.style.display = 'flex';
          }}
        />
        <span className="ai-connector-provider-logo ai-connector-provider-logo--icon" style={{ background: provider.color, display: 'none' }}>
          <MaterialIcon icon={provider.icon} size={20} />
        </span>
      </span>
    );
  }
  return (
    <span className="ai-connector-provider-logo ai-connector-provider-logo--icon" style={{ background: provider.color }}>
      <MaterialIcon icon={provider.icon} size={20} />
    </span>
  );
}

export default function AiConnectors({ setIsAuthenticated, darkMode, toggleDarkMode }: LayoutProps) {
  const [list, setList] = useState<AiConnectorItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [addingProvider, setAddingProvider] = useState<string | null>(null);
  const [editing, setEditing] = useState<AiConnectorItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AiConnectorItem | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ provider: 'openai', name: '', accessToken: '', baseUrl: '' });

  useEffect(() => {
    getAiConnectors()
      .then(setList)
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  }, []);

  const openAdd = (providerId: string) => {
    setAddingProvider(providerId);
    const p = PROVIDERS.find((x) => x.id === providerId);
    setForm({
      provider: providerId,
      name: '',
      accessToken: '',
      baseUrl: '',
    });
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.accessToken.trim()) {
      toast.error('Enter API key');
      return;
    }
    setSaving(true);
    try {
      await createAiConnector(form);
      setList(await getAiConnectors());
      setAddingProvider(null);
      setForm({ provider: 'openai', name: '', accessToken: '', baseUrl: '' });
      toast.success('Connector added');
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'response' in err && err.response && typeof (err.response as { data?: { error?: string } })?.data?.error === 'string'
        ? (err.response as { data: { error: string } }).data.error
        : err instanceof Error ? err.message : 'Error';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (c: AiConnectorItem) => {
    setEditing(c);
    setForm({
      provider: c.provider,
      name: c.name || '',
      accessToken: '',
      baseUrl: c.baseUrl || '',
    });
  };

  const saveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    setSaving(true);
    try {
      await updateAiConnector(editing._id, {
        name: form.name,
        baseUrl: form.baseUrl,
        ...(form.accessToken.trim() ? { accessToken: form.accessToken } : {}),
      });
      setList(await getAiConnectors());
      setEditing(null);
      setForm({ provider: 'openai', name: '', accessToken: '', baseUrl: '' });
      toast.success('Connector updated');
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'response' in err && err.response && typeof (err.response as { data?: { error?: string } })?.data?.error === 'string'
        ? (err.response as { data: { error: string } }).data.error
        : err instanceof Error ? err.message : 'Error';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout setIsAuthenticated={setIsAuthenticated} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
        <div className="loading">Loading...</div>
      </Layout>
    );
  }

  const grouped = PROVIDERS.map((p) => ({
    provider: p,
    items: (list || []).filter((c) => c.provider === p.id),
  }));

  return (
    <Layout setIsAuthenticated={setIsAuthenticated} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
      <div className="ai-connectors-page">
        <header className="workflows-page__header ai-connectors-page__header">
          <div>
            <h1 className="workflows-page__title">
              <MaterialIcon icon="smart_toy" size={28} style={{ marginRight: 8, verticalAlign: 'middle' }} />
              AI Connectors
            </h1>
            <p className="workflows-page__subtitle">
              Add API keys for AI services (OpenAI, Anthropic, Google, …). They are used in n8n workflows in Agent and AI Model nodes.
            </p>
          </div>
        </header>

        <div className="ai-connectors-grid-wrap">
          {grouped.map(({ provider: p, items }) => (
            <div key={p.id} className="feature-card feature-card--start is-visible ai-connector-provider-group">
              <div className="feature-card__glow" />
              <div className="feature-card__content">
                <div className="ai-connector-group-head">
                  <div className="ai-connector-group-head__brand">
                    <span className="feature-card__icon-wrapper">
                      <ProviderLogo provider={p} />
                    </span>
                    <div>
                      <span className="ai-connector-provider-name">{p.name}</span>
                      <span className="ai-connector-provider-sub">{p.subtitle}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="workflows-page__new-btn"
                    onClick={() => openAdd(p.id)}
                  >
                    <MaterialIcon icon="add" size={18} />
                    Add key
                  </button>
                </div>
                <div className="ai-connector-cells">
                {items.length === 0 ? (
                  <div className="ai-connector-cell ai-connector-cell--empty">
                    <span className="muted">No keys yet</span>
                  </div>
                ) : (
                  items.map((c, idx) => (
                    <div key={c._id} className="ai-connector-cell">
                      <div className="ai-connector-cell__main">
                        <div className="ai-connector-cell__title">
                          {c.name?.trim() ? c.name : `API key #${idx + 1}`}
                          {c.isActive === false && <span className="ai-connector-entry__badge">Disabled</span>}
                        </div>
                        {c.baseUrl ? (
                          <div className="ai-connector-cell__meta">
                            <MaterialIcon icon="dns" size={14} />
                            <span className="ai-connector-entry__url">{c.baseUrl}</span>
                          </div>
                        ) : (
                          <div className="ai-connector-cell__meta muted">Default base URL</div>
                        )}
                      </div>
                      <div className="ai-connector-cell__actions">
                        <button type="button" className="workflows-page__new-btn ai-connector-cell__btn" onClick={() => openEdit(c)} title="Edit">
                          <MaterialIcon icon="edit" size={18} />
                        </button>
                        <button
                          type="button"
                          className="workflows-page__new-btn ai-connector-cell__btn"
                          onClick={() => { setDeleteTarget(c); setDeleteModalOpen(true); }}
                          title="Remove"
                        >
                          <MaterialIcon icon="delete" size={18} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {addingProvider && (
        <div className="modal-overlay" onClick={() => !saving && setAddingProvider(null)}>
          <div className="modal-content webhook-modal pro-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pro-modal__header">
              <h2 className="pro-modal__title">Add {PROVIDERS.find((p) => p.id === addingProvider)?.name ?? addingProvider}</h2>
              <button type="button" className="pro-modal__close" onClick={() => !saving && setAddingProvider(null)} aria-label="Close">
                <MaterialIcon icon="close" size={20} />
              </button>
            </div>
            <form onSubmit={save}>
              <div className="pro-modal__body">
                <p className="muted small" style={{ margin: 0 }}>
                  You can add multiple keys per provider (e.g. rotation, different projects).
                </p>
                <label>
                  Name (optional)
                  <input
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="My connector"
                  />
                </label>
                <label>
                  API key / Access token *
                  <input
                    type="password"
                    value={form.accessToken}
                    onChange={(e) => setForm((f) => ({ ...f, accessToken: e.target.value }))}
                    placeholder={PROVIDERS.find((p) => p.id === form.provider)?.placeholder}
                  />
                </label>
                {(form.provider === 'custom' || form.provider === 'openai') && (
                  <label>
                    Base URL (optional)
                    <input
                      value={form.baseUrl}
                      onChange={(e) => setForm((f) => ({ ...f, baseUrl: e.target.value }))}
                      placeholder="https://…"
                    />
                  </label>
                )}
              </div>
              <div className="pro-modal__footer">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editing && (
        <div className="modal-overlay" onClick={() => !saving && setEditing(null)}>
          <div className="modal-content webhook-modal pro-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pro-modal__header">
              <h2 className="pro-modal__title">Edit connector</h2>
              <button type="button" className="pro-modal__close" onClick={() => !saving && setEditing(null)} aria-label="Close">
                <MaterialIcon icon="close" size={20} />
              </button>
            </div>
            <form onSubmit={saveEdit}>
              <div className="pro-modal__body">
                <label>
                  Name (optional)
                  <input
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="My connector"
                  />
                </label>
                <label>
                  New API key (optional)
                  <input
                    type="password"
                    value={form.accessToken}
                    onChange={(e) => setForm((f) => ({ ...f, accessToken: e.target.value }))}
                    placeholder="Leave empty to keep existing"
                  />
                </label>
                {(form.provider === 'custom' || form.provider === 'openai') && (
                  <label>
                    Base URL (optional)
                    <input
                      value={form.baseUrl}
                      onChange={(e) => setForm((f) => ({ ...f, baseUrl: e.target.value }))}
                      placeholder="https://…"
                    />
                  </label>
                )}
              </div>
              <div className="pro-modal__footer">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteModalOpen && deleteTarget && (
        <div
          className="modal-overlay"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001 }}
          onClick={() => { setDeleteModalOpen(false); setDeleteTarget(null); }}
        >
          <div className="modal" style={{ background: '#252525', padding: 24, borderRadius: 8, minWidth: 360 }} onClick={(e) => e.stopPropagation()}>
            <h3>Naozaj zmazať AI connector?</h3>
            <p style={{ marginTop: 8, color: '#aaa' }}>
              Connector „{deleteTarget.name || deleteTarget.provider}“ bude natrvalo zmazaný. Túto akciu nemožno vrátiť späť.
            </p>
            <div style={{ marginTop: 24, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={() => { setDeleteModalOpen(false); setDeleteTarget(null); }}>Zrušiť</button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={async () => {
                  if (!deleteTarget) return;
                  try {
                    await deleteAiConnector(deleteTarget._id);
                    setList((l) => l.filter((c) => c._id !== deleteTarget._id));
                    toast.success('Connector removed');
                  } catch (err: unknown) {
                    const message = err && typeof err === 'object' && 'response' in err && err.response && typeof (err.response as { data?: { error?: string } })?.data?.error === 'string'
                      ? (err.response as { data: { error: string } }).data.error
                      : err instanceof Error ? err.message : 'Error';
                    toast.error(message);
                  } finally {
                    setDeleteModalOpen(false);
                    setDeleteTarget(null);
                  }
                }}
              >
                Zmazať
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
